import type { MessageBus } from "../core/bus";
import type { BusMessage, BusPayload, ConnectionState, DeviceDescriptor } from "../core/messages";
import { decodeLine, encodeCommand, type ParseMode } from "../core/codec";
import { parseDescriptor } from "../core/messages";

/**
 * Transport adapter contract — the Node-RED node taxonomy, flattened.
 * Every adapter normalizes its wire format into BusMessages on
 * `<device>/<signal>` and consumes commands published on device topics.
 *
 * Adapters must be event-driven (no polling loops) so they keep working
 * in backgrounded tabs.
 */

export interface AdapterConfig {
  device: string;
  bus: MessageBus;
  parse: ParseMode;
  /** Extra transport options straight off the gr-connect attributes. */
  options: Record<string, string>;
  onState: (state: ConnectionState, detail?: string) => void;
  onDescriptor?: (descriptor: DeviceDescriptor | null) => void;
}

export interface Adapter {
  /** Must be called from a user gesture where the transport requires one. */
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  readonly state: ConnectionState;
}

export type AdapterFactory = (config: AdapterConfig) => Adapter;

export interface TransportField {
  name: string;
  label: string;
  placeholder: string;
  type?: "text" | "password";
  /** Safe to retain in localStorage between visits. Defaults to false. */
  persist?: boolean;
  /** Reject credential-bearing URL forms before persisting. */
  cleanUrl?: boolean;
  /** Trim UI input before connecting. Defaults to true. */
  trim?: boolean;
}

export interface TransportDefinition {
  name: string;
  label?: string;
  fields?: TransportField[];
  requiresGesture?: boolean;
  supported?: () => boolean;
  create: AdapterFactory;
}

const registry = new Map<string, TransportDefinition>();

export function registerAdapter(definition: TransportDefinition): void {
  if (!definition.name || registry.has(definition.name)) {
    throw new Error(`[granrojo] duplicate or empty transport "${definition.name}"`);
  }
  registry.set(definition.name, definition);
}

export function createAdapter(transport: string, config: AdapterConfig): Adapter {
  const definition = registry.get(transport);
  if (!definition) {
    const known = [...registry.keys()].join(", ");
    throw new Error(`[granrojo] unknown transport "${transport}" (available: ${known})`);
  }
  return definition.create(config);
}

export function availableTransports(): string[] {
  return [...registry.keys()];
}

export function transportDefinition(transport: string): TransportDefinition | undefined {
  return registry.get(transport);
}

export function transportDefinitions(): TransportDefinition[] {
  return [...registry.values()];
}

/**
 * Shared line-oriented plumbing used by serial and websocket adapters:
 * decode inbound lines to bus topics, encode command subscriptions to
 * outbound lines, run the DESCRIBE handshake.
 */
export class LineProtocolBinding {
  private unsubscribers: Array<() => void> = [];
  private descriptorResolved = false;
  readonly srcTag: string;

  constructor(
    private config: AdapterConfig,
    private writeLine: (line: string) => void,
  ) {
    this.srcTag = `adapter:${config.device}`;
  }

  /** Wire command topics → outbound lines. Call once on connect. */
  attach(): void {
    const { bus, device } = this.config;
    this.unsubscribers.push(
      bus.subscribe(
        `${device}/$send`,
        (msg) => {
          if (msg.src === this.srcTag) return;
          this.writeLine(String(msg.payload));
        },
        { replay: false },
      ),
      bus.subscribe(
        `${device}/+`,
        (msg) => {
          if (msg.src === this.srcTag) return;
          const signal = msg.topic.slice(device.length + 1);
          if (signal.startsWith("$")) return;
          this.writeLine(encodeCommand(signal, msg.payload));
        },
        { replay: false },
      ),
    );
  }

  detach(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }

  requestDescriptor(): void {
    this.descriptorResolved = false;
    this.writeLine("DESCRIBE?");
  }

  /** Route one inbound line to the bus. */
  handleLine(line: string): void {
    const { bus, device, parse } = this.config;
    if (line.length === 0) return;
    bus.publish(`${device}/$raw`, line, { src: this.srcTag, retain: false });

    if (line.startsWith("DESCRIBE:")) {
      this.resolveDescriptor(line.slice("DESCRIBE:".length));
      return;
    }

    const decoded = decodeLine(line, parse);
    for (const [key, value] of Object.entries(decoded.values)) {
      bus.publish(`${device}/${key}`, value as BusPayload, { src: this.srcTag });
    }
  }

  private resolveDescriptor(json: string): void {
    if (this.descriptorResolved) return;
    this.descriptorResolved = true;
    let descriptor: DeviceDescriptor | null = null;
    try {
      descriptor = parseDescriptor(JSON.parse(json));
    } catch {
      descriptor = null;
    }
    this.config.onDescriptor?.(descriptor);
  }
}

export type { BusMessage };
